import { useCallback, useRef } from "react";

/**
 * Keeps the click that ends a rename from also opening a report. Pressing
 * outside the name input blurs it, which commits the rename and clears the
 * renaming state before the click lands, so by then the click looks like an
 * ordinary one. The press is the last moment the state still says renaming.
 */
export const useRenameClickGuard = (isRenaming: boolean) => {
  const pressedWhileRenamingRef = useRef(false);

  // Every press overwrites the flag, so one whose click never arrived (a drag
  // off the row, a press on the menu) cannot swallow a later click.
  const onPointerDownCapture = useCallback(() => {
    pressedWhileRenamingRef.current = isRenaming;
  }, [isRenaming]);

  // A click that never reaches the row (one the action menu stops) still
  // passes here on its way down. The flag goes once the click is done, so it
  // cannot swallow a later keyboard click on a row.
  const onClickCapture = useCallback(() => {
    setTimeout(() => {
      pressedWhileRenamingRef.current = false;
    });
  }, []);

  /** True when this click ended a rename and must not open the report. */
  const consumeRenameClick = useCallback((event?: { detail: number }) => {
    const wasPressedWhileRenaming = pressedWhileRenamingRef.current;
    pressedWhileRenamingRef.current = false;
    // A keyboard-initiated click has no press, so a stale flag is not its own.
    if (event?.detail === 0) return false;
    return wasPressedWhileRenaming;
  }, []);

  return { onPointerDownCapture, onClickCapture, consumeRenameClick };
};
